import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IQuestionnaireQuestion } from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
  selector: 'app-stroke-score-mediterranean-questions',
  templateUrl: './stroke-score-mediterranean-questions.component.html',
  styleUrl: './stroke-score-mediterranean-questions.component.css',
  standalone: false
})
export class StrokeScoreMediterraneanQuestionsComponent implements OnInit {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  private readonly totalControlName = 'question62';
  
  constructor(
    private fb: FormBuilder,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService
  ) {}

  isInpatientView(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  ngOnInit() {
    // Ensure total exists + is disabled
    const totalCtrl = this.formGroup.get(this.totalControlName);
    if (totalCtrl) {
      totalCtrl.disable({ emitEvent: false });
    }

    // Recalculate on any changes
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateTotal();
    });

    // Initial
    this.recalculateTotal();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  setNotMentioned(fieldName: string): void {
    this.formGroup
      .get(fieldName)
      ?.setValue(this.translate.instant('Not mentioned'));
    this.cdr.detectChanges(); // Ensure UI updates immediately
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;

    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null); // unselect
    } else {
      control.setValue(value);
    }
  }

  private recalculateTotal(): void {
    const totalCtrl = this.formGroup.get(this.totalControlName);
    if (!totalCtrl) return;

    let total = 0;

    for (const el of this.elements ?? []) {
      if (!el?.name) continue;
      if (el.name === this.totalControlName) continue;

      // only scored types
      if (el.type !== 'radiogroup' && el.type !== 'boolean') continue;

      const selectedValue = this.formGroup.get(el.name)?.value;
      if (selectedValue == null) continue;

      let selectedText = '';

      if (el.type === 'radiogroup') {
        const choice = (el.choices ?? []).find((c: any) => c.value === selectedValue);
        selectedText = choice?.text ?? '';
      } else {
        // if you ever include points in boolean labels
        selectedText = selectedValue ? (el.labelTrue ?? '') : (el.labelFalse ?? '');
      }

      total += this.extractPoints(selectedText);
    }

    // Write it back as text, keep disabled
    totalCtrl.setValue(String(total), { emitEvent: false });
    totalCtrl.disable({ emitEvent: false });
    this.cdr.detectChanges();
  }

  private extractPoints(text: string): number {
    if (!text) return 0;

    // If DB text might be a translation key, resolve it (safe either way)
    const resolved = this.translate.instant(text);
    const s = String(resolved ?? text);

    // Matches: "(2 points)" "(1 point)" "(2 βαθμοί)" "(1 βαθμός)"
    const m = s.match(/\(\s*(\d+)\s*(?:point|points|βαθμ(?:ός|οί))\s*\)/i);
    return m ? Number(m[1]) : 0;
  }
}
